import type { TradeKey } from '../../utils/categoryToTrade';

export type PlantafelViewMode = 'tag' | 'woche' | 'monat';

/** Vorausgefüllter Termin nach Ziehen auf der Plantafel-Zeitleiste. */
export type PlantafelCreateDraft = {
  due_at: string;
  end_at: string;
  lead_id?: string | null;
  assignee_employee_ids?: string[];
};

/** Max. Spalten in der Monatsansicht (6 Kalenderwochen). */
export const MONAT_MAX_WEEKS = 6;
export const MONAT_MAX_DAYS = MONAT_MAX_WEEKS * 7;

/** Full-day range in stored block data (e.g. absences); viewport hours come from settings. */
export const HOUR_START = 0;
export const HOUR_END = 24;
export const HOUR_SPAN = HOUR_END - HOUR_START;
export const RESOURCE_COL_W = 248;
export const DAY_HEADER_H = 60;
export const SECTION_HEAD_H = 30;
/** Standardhöhe einer Plantafel-Zeile (ein Termin). */
export const PLANTAFEL_ROW_BASE_H = 60;
/** Zusätzliche Zeilenhöhe pro weiterem überlappendem Termin. */
export const PLANTAFEL_ROW_LANE_EXTRA_H = 52;
/** Höhe der Projekt-Kopfzeile (Gruppierung nach Projekt, ohne Termine). */
export const PROJECT_HEADER_ROW_H = 48;

export type SchedulingStatus = 'geplant' | 'bestaetigt' | 'laufend' | 'abgeschlossen' | 'konflikt';

export const SCHEDULING_STATUS_LABELS: Record<SchedulingStatus, string> = {
  geplant: 'Geplant',
  bestaetigt: 'Bestätigt',
  laufend: 'Läuft',
  abgeschlossen: 'Abgeschlossen',
  konflikt: 'Konflikt',
};

export const SCHEDULING_STATUS_FILTER_OPTIONS: ReadonlyArray<{ id: SchedulingStatus; label: string }> = (
  Object.entries(SCHEDULING_STATUS_LABELS) as [SchedulingStatus, string][]
).map(([id, label]) => ({ id, label }));

export type PlantafelMembershipRole = 'owner' | 'admin' | 'member';

export type BoardEmployee = {
  id: string;
  name: string;
  initials: string;
  role: string;
  membership_role?: PlantafelMembershipRole | null;
  color_idx: number;
  color: string;
  capacity_hours: number;
  expected_hours_per_day: number;
};

export type ProfileAssignee = {
  user_id: string;
  display_name: string | null;
  email: string | null;
};

export type EmployeeAssignee = {
  id: string;
  first_name: string;
  last_name: string;
  color_idx: number;
  position: string;
};

export type BoardActivity = {
  id: string;
  lead_id: string | null;
  title: string;
  activity_type: string;
  checklist_id: string | null;
  due_at: string | null;
  end_at: string | null;
  priority: string | null;
  notes: string | null;
  done: boolean;
  scheduling_status: SchedulingStatus;
  primary_employee_id: string | null;
  lead_display_id: string | null;
  lead_name: string | null;
  lead_title: string | null;
  lead_category: string | null;
  activity_site_address: string | null;
  lead_site_address: string | null;
  lead_site_latitude: number | null;
  lead_site_longitude: number | null;
  lead_phone: string | null;
  assignee_users: ProfileAssignee[];
  assignee_employees: EmployeeAssignee[];
  series_id?: string | null;
  recurrence?: unknown;
  original_start_at?: string | null;
  series_exception?: boolean;
};

export type AbsenceKind =
  | 'urlaub'
  | 'krank'
  | 'feiertag'
  | 'freischicht'
  | 'stundenabbau'
  | 'fehlzeit'
  | 'sonderurlaub'
  | 'krankengeld';

/** Filterbare Abwesenheitsarten in der Plantafel-Toolbar. */
export type AbsenceFilterKind =
  | 'urlaub'
  | 'krank'
  | 'freischicht'
  | 'stundenabbau'
  | 'fehlzeit'
  | 'sonderurlaub'
  | 'krankengeld';

export const ABSENCE_FILTER_OPTIONS: ReadonlyArray<{ value: AbsenceFilterKind; label: string }> = [
  { value: 'urlaub', label: 'Urlaub' },
  { value: 'krank', label: 'AU' },
  { value: 'freischicht', label: 'Freischicht' },
  { value: 'stundenabbau', label: 'Freizeitausgleich' },
  { value: 'fehlzeit', label: 'Fehlzeit' },
  { value: 'sonderurlaub', label: 'Sonderurlaub' },
  { value: 'krankengeld', label: 'Krankengeld' },
];

/** Prefixes for the unified Plantafel type filter (activity + absence). */
export const PLANTAFEL_TYPE_FILTER_ACTIVITY_PREFIX = 'a:';
export const PLANTAFEL_TYPE_FILTER_ABSENCE_PREFIX = 'abs:';

export function plantafelTypeFilterActivityId(activityTypeId: string): string {
  return `${PLANTAFEL_TYPE_FILTER_ACTIVITY_PREFIX}${activityTypeId}`;
}

export function plantafelTypeFilterAbsenceId(kind: AbsenceFilterKind): string {
  return `${PLANTAFEL_TYPE_FILTER_ABSENCE_PREFIX}${kind}`;
}

/** Unified type-filter options (activities + absences), sorted A–Z by label. */
export function buildPlantafelTypeFilterOptions(
  activityTypes: ReadonlyArray<{ id: string; label: string }>,
): Array<{ id: string; label: string }> {
  return [
    ...activityTypes.map((t) => ({
      id: plantafelTypeFilterActivityId(t.id),
      label: t.label,
    })),
    ...ABSENCE_FILTER_OPTIONS.map((o) => ({
      id: plantafelTypeFilterAbsenceId(o.value),
      label: o.label,
    })),
  ].sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }));
}
export type PublicHoliday = {
  holiday_date: string;
  name: string;
};

export type AbsenceBlock = {
  id: string;
  employee_id: string;
  entry_date: string;
  entry_type: string;
  absence_kind: AbsenceKind | 'other';
  start_time: string | null;
  end_time: string | null;
  break_minutes: number | null;
  holiday_name?: string | null;
};

export type UtilizationDay = {
  day: string;
  scheduled_hours: number;
  capacity_hours: number;
  pct: number;
};

export type GridBlock = {
  id: string;
  activityId: string;
  leadId: string | null;
  title: string;
  customer: string;
  trade: TradeKey;
  activityType: string;
  status: SchedulingStatus;
  priority: string | null;
  dayStart: number;
  dayEnd: number;
  hourStart: number;
  hourEnd: number;
  /**
   * Unclipped real schedule (ISO). Labels/tooltips use this; day/hour fields stay
   * viewport-clipped for layout.
   */
  scheduleDueAt?: string;
  scheduleEndAt?: string;
  assigneeEmployeeIds: string[];
  primaryEmployeeId: string | null;
  orderNo: string | null;
  isAbsence?: AbsenceKind;
  /** Geleistete Stunden bei zeitgebundenem Freizeitausgleich (overtime_comp). */
  absenceTimedHours?: number;
  /** Vorschau-Balken nach Ziehen, bis der Termin gespeichert wird. */
  isPendingCreate?: boolean;
  /** Teil einer Wiederholungsserie. */
  isRecurring?: boolean;
};

export const PENDING_CREATE_BLOCK_ID = 'pending-create';

/** Sentinel lead id for activities without a linked project (lead). */
export const PLANTAFEL_ORPHAN_LEAD_ID = '__ohne_projekt__';

export function plantafelBlockLeadId(leadId: string | null | undefined): string {
  return leadId ?? PLANTAFEL_ORPHAN_LEAD_ID;
}

export type ProjectRow = {
  key: string;
  leadId: string;
  title: string;
  customer: string;
  trade: TradeKey;
  assigneeEmployeeIds: string[];
};
